import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { Button } from '../../components/ui/Button';
import { Card, CardHeader } from '../../components/ui/Card';
import { ResponsiveContainer } from '../../components/ui/ResponsiveContainer';
import { AdminLoginRequest } from '../../services/api';

/**
 * 로그인 페이지
 * - 사용자 인증
 * - 폼 유효성 검사
 * - 에러 처리
 */
export function LoginPage() {
  const navigate = useNavigate();
  const { adminLogin, isLoggingIn, loginError, isAuthenticated, adminLoginMutation } = useAuth();

  const [formData, setFormData] = useState({
    adminId: '',
    adminPassword: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // 폼 데이터 변경
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));

    // 에러 클리어
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  // 폼 유효성 검사
  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.adminId) {
      newErrors.adminId = '관리자 ID를 입력해주세요.';
    }

    if (!formData.adminPassword) {
      newErrors.adminPassword = '비밀번호를 입력해주세요.';
    } else if (formData.adminPassword.length < 6) {
      newErrors.adminPassword = '비밀번호는 6자 이상이어야 합니다.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // 로그인 처리
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    // Admin 로그인 API 호출
    const loginData: AdminLoginRequest = {
      adminId: formData.adminId,
      adminPassword: formData.adminPassword,
    };

    // useAuth의 adminLogin 함수 호출 (뮤테이션)
    adminLogin(loginData);
  };

  // 로그인 성공 시 리다이렉트
  React.useEffect(() => {
    if (isAuthenticated) {
      console.log('로그인 성공, 대시보드로 이동');
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  // 로그인 에러 처리
  React.useEffect(() => {
    if (loginError) {
      console.error('로그인 에러:', loginError);
      setErrors({
        general: loginError.message || '로그인에 실패했습니다. 관리자 ID와 비밀번호를 확인해주세요.',
      });
    }
  }, [loginError]);

  // 뮤테이션 상태 디버깅
  React.useEffect(() => {
    console.log('Admin 로그인 뮤테이션 상태:', {
      isPending: adminLoginMutation.isPending,
      isSuccess: adminLoginMutation.isSuccess,
      isError: adminLoginMutation.isError,
      error: adminLoginMutation.error,
      data: adminLoginMutation.data,
    });
  }, [adminLoginMutation.isPending, adminLoginMutation.isSuccess, adminLoginMutation.isError, adminLoginMutation.error, adminLoginMutation.data]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4 py-12 sm:px-6 lg:px-8">
      <ResponsiveContainer maxWidth="sm" padding="none">
        <Card className="w-full">
          <CardHeader>
            <div className="text-center">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-primary-500">
                <span className="text-xl font-bold text-white">멍</span>
              </div>
              <h2 className="text-2xl font-bold text-gray-900">
                멍투데이 관리자
              </h2>
              <p className="mt-2 text-sm text-gray-600">계정에 로그인하세요</p>
            </div>
          </CardHeader>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* 관리자 ID 입력 */}
            <div>
              <label
                htmlFor="adminId"
                className="block text-sm font-medium text-gray-700"
              >
                관리자 ID
              </label>
              <input
                id="adminId"
                name="adminId"
                type="text"
                autoComplete="username"
                value={formData.adminId}
                onChange={handleChange}
                className={`mt-1 block w-full rounded-md border px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-primary-500 ${
                  errors.adminId ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="관리자 ID를 입력하세요"
              />
              {errors.adminId && (
                <p className="mt-1 text-sm text-red-600">{errors.adminId}</p>
              )}
            </div>

            {/* 비밀번호 입력 */}
            <div>
              <label
                htmlFor="adminPassword"
                className="block text-sm font-medium text-gray-700"
              >
                비밀번호
              </label>
              <input
                id="adminPassword"
                name="adminPassword"
                type="password"
                autoComplete="current-password"
                value={formData.adminPassword}
                onChange={handleChange}
                className={`mt-1 block w-full rounded-md border px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-primary-500 ${
                  errors.adminPassword ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="비밀번호를 입력하세요"
              />
              {errors.adminPassword && (
                <p className="mt-1 text-sm text-red-600">{errors.adminPassword}</p>
              )}
            </div>

            {/* 로그인 에러 표시 */}
            {(loginError || errors.general) && (
              <div className="rounded-md border border-red-200 bg-red-50 p-3">
                <p className="text-sm text-red-600">
                  {loginError?.message || errors.general || '로그인에 실패했습니다.'}
                </p>
              </div>
            )}

            {/* 로그인 버튼 */}
            <Button
              type="submit"
              fullWidth
              loading={isLoggingIn}
              disabled={isLoggingIn}
            >
              {isLoggingIn ? '로그인 중...' : '로그인'}
            </Button>
          </form>

          {/* 추가 링크 */}
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              계정이 없으신가요?{' '}
              <a
                href="#"
                className="font-medium text-primary-600 hover:text-primary-500"
              >
                관리자에게 문의하세요
              </a>
            </p>
          </div>
        </Card>
      </ResponsiveContainer>
    </div>
  );
}
